import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Heart, Leaf, Flame } from 'lucide-react';

export default function AboutPage() {
  const { t, lang } = useLanguage();

  const values = [
    { icon: Leaf, title: t('about.values.quality'), desc: t('about.values.quality.desc'), color: '#2E7D32' },
    { icon: Heart, title: t('about.values.tradition'), desc: t('about.values.tradition.desc'), color: '#e53a3a' },
    { icon: Flame, title: t('about.values.passion'), desc: t('about.values.passion.desc'), color: '#f4d00d' },
  ];

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/assets/images/fallback/pro-QAXclyf3.jpeg" alt="Restaurant" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            {t('about.title')}
          </motion.h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img src="https://www.restobelgo.com/assets/images/site/500a0df5-2c89-48c5-9c65-ba47bb34314b-460b2026.jpg" alt="Notre équipe" className="rounded-xl shadow-lg w-full h-80 object-cover" />
              </div>
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-6">
                  {lang === 'fr' ? 'Le Cochon Braisé 15 Heures' : 'The 15-Hour Braised Pork'}
                </h2>
                <p className="text-[#555555] leading-relaxed mb-4">{t('about.p1')}</p>
                <p className="text-[#555555] leading-relaxed">{t('about.p2')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1a1a1a] text-center mb-12">{t('about.values.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                className="text-center p-8"
                data-testid={`value-card-${v.title}`}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${v.color}15` }}>
                  <v.icon className="w-8 h-8" style={{ color: v.color }} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1a1a1a] mb-2">{v.title}</h3>
                <p className="text-sm text-[#555555]">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours Section */}
      <section className="py-20" data-testid="hours-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-8">{t('contact.hours')}</h2>
          <div className="bg-white rounded-xl border border-[#e0e0e0] p-8 shadow-sm">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                { day: { fr: 'Lundi', en: 'Monday' }, hours: { fr: 'Fermé', en: 'Closed' } },
                { day: { fr: 'Mardi', en: 'Tuesday' }, hours: { fr: 'Fermé', en: 'Closed' } },
                { day: { fr: 'Mercredi', en: 'Wednesday' }, hours: '11h - 19h' },
                { day: { fr: 'Jeudi', en: 'Thursday' }, hours: '11h - 21h' },
                { day: { fr: 'Vendredi', en: 'Friday' }, hours: '11h - 22h' },
                { day: { fr: 'Samedi', en: 'Saturday' }, hours: '11h - 21h' },
                { day: { fr: 'Dimanche', en: 'Sunday' }, hours: '11h - 20h' },
              ].map((d) => (
                <div key={typeof d.day === 'object' ? d.day.en : d.day} className={`flex justify-between py-2 border-b border-[#e0e0e0] last:border-b-0 col-span-2`}>
                  <span className="font-medium text-[#1a1a1a]">{typeof d.day === 'object' ? d.day[lang] : d.day}</span>
                  <span className={`${typeof d.hours === 'object' ? 'text-[#555555]' : 'text-[#e53a3a] font-medium'}`}>
                    {typeof d.hours === 'object' ? d.hours[lang] : d.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
